/*
  PACK DRAWER
  ------------------------------------------------------------
  The buy stack used to be one tap: the pack tiers sitting on the
  page with an Add to cart underneath. It is two taps now — a
  "Choose package" button on the page, and a sheet that rises from
  the bottom of the viewport carrying the tiers and the real Add
  to cart.

  Nothing about the purchase changed. The tiers and the submit
  button are the same theme blocks with the same markup and the
  same handlers; they were moved inside a container block, and
  this file does no more than lift that container off the page
  and set it back down. The tier radios bind to the product form
  through their form="" attribute rather than through the DOM
  tree, so relocating them costs the form nothing.

  The container tags itself .pack-drawer from an inline script in
  its first child block, which runs while the browser is still
  parsing the blocks below it. That is deliberate: if the class
  arrived here, on defer, the tiers would paint inline for a frame
  before being pulled off the page. It also means that with
  JavaScript off the class is never added, the sheet never leaves
  the page, and the buy stack degrades to exactly what it was.
  ------------------------------------------------------------ */

(function () {
  'use strict';

  var OPEN_CLASS = 'pack-drawer--open';
  var OVERLAY_OPEN_CLASS = 'pack-drawer__overlay--open';
  var LOCK_CLASS = 'pack-drawer-locked';
  var HOST_CLASS = 'pack-drawer-host';

  /* Long enough to outlast the slide in clinical.css. Only used to
     hold the desktop stacking context up while the sheet leaves. */
  var SLIDE_MS = 400;

  var FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  var drawer = null;
  var overlay = null;
  var host = null;
  var lastFocused = null;
  var hostTimer = null;

  function findDrawer() {
    if (!drawer || !document.contains(drawer)) {
      drawer = document.querySelector('.pack-drawer');
    }
    return drawer;
  }

  function isOpen() {
    var el = findDrawer();
    return !!el && el.classList.contains(OPEN_CLASS);
  }

  /* The overlay is a sibling of the sheet rather than a child of
     <body>. Above 750px the theme puts the whole product column in
     its own stacking context, and an overlay parked on the body
     would paint on the far side of it from the sheet — dimming the
     page but also dimming the sheet, or sliding under it. Kept as a
     sibling, the two always share a context and always order. */
  function ensureOverlay(el) {
    if (overlay && document.contains(overlay)) return overlay;
    overlay = document.createElement('div');
    overlay.className = 'pack-drawer__overlay';
    overlay.addEventListener('click', function () {
      close();
    });
    el.parentNode.insertBefore(overlay, el);
    return overlay;
  }

  function visibleFocusables(el) {
    return Array.prototype.filter.call(el.querySelectorAll(FOCUSABLE), function (node) {
      return node.offsetWidth > 0 || node.offsetHeight > 0 || node === document.activeElement;
    });
  }

  function onKeydown(event) {
    if (event.key === 'Escape' || event.key === 'Esc') {
      event.preventDefault();
      close();
      return;
    }

    if (event.key !== 'Tab') return;

    var el = findDrawer();
    if (!el) return;

    var stops = visibleFocusables(el);
    if (!stops.length) return;

    var first = stops[0];
    var last = stops[stops.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    } else if (!el.contains(document.activeElement)) {
      event.preventDefault();
      first.focus();
    }
  }

  function open(trigger) {
    var el = findDrawer();
    if (!el || isOpen()) return;

    window.clearTimeout(hostTimer);
    ensureOverlay(el);
    lastFocused = trigger || document.activeElement;

    /* Above 750px the product column is a sticky, z-indexed block,
       so a fixed child of it cannot climb over the masthead on its
       own. Raise the column itself for as long as the sheet is up.
       Below 750px there is no such column and this is a no-op. */
    host = el.closest('.product__column-sticky');
    if (host) host.classList.add(HOST_CLASS);

    document.documentElement.classList.add(LOCK_CLASS);
    el.removeAttribute('inert');
    el.setAttribute('aria-hidden', 'false');

    /* Read back a layout value so the browser commits the closed
       transform before the open one lands, or the sheet appears
       already seated instead of rising. */
    void el.offsetHeight;

    overlay.classList.add(OVERLAY_OPEN_CLASS);
    el.classList.add(OPEN_CLASS);

    /* Focus waits for the sheet to be visible, not merely open.
       focus() on an element whose computed visibility is still hidden
       fails silently, and a keyboard user would be left tabbing the
       page behind an open modal. transitionend is the honest signal;
       the timer covers the cases where no transition runs at all —
       reduced motion, a backgrounded tab, a browser that skipped it.

       The sheet takes focus rather than its close button: "Close"
       announced as the first thing in a dialog tells a screen reader
       user how to leave a sheet before telling them they are in one. */
    var focusTaken = false;
    var takeFocus = function () {
      if (focusTaken || !el.classList.contains(OPEN_CLASS)) return;
      focusTaken = true;
      el.focus();
    };
    el.addEventListener('transitionend', takeFocus, { once: true });
    window.setTimeout(takeFocus, SLIDE_MS);

    document.addEventListener('keydown', onKeydown, true);
  }

  function close() {
    var el = findDrawer();
    if (!el || !isOpen()) return;

    el.classList.remove(OPEN_CLASS);
    el.setAttribute('aria-hidden', 'true');
    el.setAttribute('inert', '');
    if (overlay) overlay.classList.remove(OVERLAY_OPEN_CLASS);
    document.documentElement.classList.remove(LOCK_CLASS);
    document.removeEventListener('keydown', onKeydown, true);

    /* Drop the raised context only once the sheet is off screen,
       otherwise it ducks behind the masthead halfway down. */
    if (host) {
      var leaving = host;
      hostTimer = window.setTimeout(function () {
        leaving.classList.remove(HOST_CLASS);
      }, SLIDE_MS);
      host = null;
    }

    if (lastFocused && document.contains(lastFocused)) lastFocused.focus();
    lastFocused = null;
  }

  /* Delegated on the document in capture so the sticky bar's own
     handler never runs: that button is wired by the theme to fire
     the main Add to cart, and it now has to ask which package
     first, like every other route into the form. */
  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      if (target.closest('[data-pack-drawer-close]')) {
        event.preventDefault();
        close();
        return;
      }

      if (isOpen()) return;

      var opener = target.closest('[data-pack-drawer-open], sticky-atc .sticky-atc__add_button');
      if (!opener || !findDrawer()) return;

      event.preventDefault();
      event.stopPropagation();
      open(opener);
    },
    true
  );

  /* Adding to cart hands the shopper to the theme's cart drawer.
     Two stacked sheets is one too many, so this one leaves as that
     one arrives. Watching the class is version-proof in a way that
     guessing the theme's event names is not. */
  function watchCart() {
    ['cart-drawer', 'cart-notification'].forEach(function (tag) {
      var node = document.querySelector(tag);
      if (!node || typeof MutationObserver !== 'function') return;

      new MutationObserver(function () {
        if (node.classList.contains('active')) close();
      }).observe(node, { attributes: true, attributeFilter: ['class'] });
    });
  }

  /* The floating bar decides when to show itself by measuring how far
     the page has scrolled past the main Add to cart: it reveals once
     scrollY clears that button's offset from the top of the document.

     That button is now inside a fixed sheet, so it has no offset from
     the top of the document any more — it sits a constant distance
     below the fold, which means the threshold slides down the page at
     exactly the speed you scroll and is never crossed. The bar simply
     never appeared.

     The trigger stands precisely where the Add to cart used to stand,
     so pointing the bar at it restores the original behaviour rather
     than approximating it. The property is found by looking for
     whichever own property holds an element inside the sheet, not by
     its name: the theme's script is minified and that name is not
     ours to rely on. */
  function repointStickyBar(el) {
    /* The bar's behaviour lives in the theme's own script, which is
       deferred and cross-origin — it may not have registered the
       element by the time this file runs, and an un-upgraded element
       has none of the properties below. Waiting on the definition is
       the only ordering guarantee available; if it never arrives
       there is no bar logic to correct. */
    if (!window.customElements || !customElements.whenDefined) return;

    customElements.whenDefined('sticky-atc').then(function () {
      applyRepoint(el);
    })['catch'](function () {});
  }

  function applyRepoint(el) {
    var bar = document.querySelector('sticky-atc');
    var trigger = document.querySelector('[data-pack-drawer-open]');
    if (!bar || !trigger) return;

    var repointed = false;
    Object.keys(bar).forEach(function (key) {
      var value = bar[key];
      if (!(value instanceof Element) || !el.contains(value)) return;
      bar[key] = trigger;
      repointed = true;
    });

    /* Its handler is bound to document scroll and reads the anchor
       fresh each time, so one synthetic event re-runs the decision
       with the new anchor instead of waiting for the shopper to move. */
    if (repointed) document.dispatchEvent(new Event('scroll'));
  }

  function init() {
    var el = findDrawer();
    if (!el) return;

    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-hidden', 'true');
    el.setAttribute('inert', '');
    /* Focusable by script only, so the sheet can take focus when it
       opens without joining the page's tab order when it has not. */
    el.setAttribute('tabindex', '-1');

    /* Point at the heading rather than copying it into a label, so
       the dialog's announced name and its visible one cannot drift
       apart when the copy changes. Falls back to a copy for the case
       where the heading has no id to point at. */
    var title = el.querySelector('.pack-drawer__title');
    if (title && title.id) {
      el.setAttribute('aria-labelledby', title.id);
      el.removeAttribute('aria-label');
    } else if (title) {
      el.setAttribute('aria-label', title.textContent.trim());
    }

    repointStickyBar(el);
    watchCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
